// Schmales lokales Booking-Shape passend zur AccountBookerBookings-Query. Bewusst
// NICHT aus dem Codegen importiert — der generierte Typ existiert erst nach dem
// Codegen-Lauf. Die neue Query liefert Buchungen verschachtelt unter
// booker.attendees[].bookings (statt der früheren flachen bookingsByUser-Liste);
// der Teilnehmername kommt daher vom Eltern-Attendee, nicht von booking.attendee.
export interface AccountBookingLessonLocation {
    name?: string | null
    city?: string | null
}

export interface AccountBookingLesson {
    startDateTime?: string | null
    endDateTime?: string | null
    location?: AccountBookingLessonLocation | null
}

export interface AccountBooking {
    id: string
    bookingType?: string | null
    // DateTime im Schema — hier reicht Truthiness (gesetzt = storniert)
    canceled?: string | boolean | null
    courseLessons?: AccountBookingLesson[] | null
}

/** Buchung mit an den Eltern-Attendee angehängtem Anzeigenamen. */
export type WithAttendeeName<B> = B & { attendeeName?: string }

/**
 * Flacht die verschachtelte Struktur der AccountBookerBookings-Query
 * (booker → attendees → bookings) zu einer Buchungsliste ab und hängt an jede
 * Buchung den Namen ihres Teilnehmers (attendeeName) aus dem Eltern-Attendee an.
 */
export const flattenBookerBookings = <B>(
    attendees:
        | ReadonlyArray<{ firstname?: string | null; lastname?: string | null; bookings?: ReadonlyArray<B> | null } | null | undefined>
        | null
        | undefined,
): Array<WithAttendeeName<B>> =>
    (attendees ?? []).flatMap((attendee) => {
        const attendeeName = [attendee?.firstname, attendee?.lastname].filter(Boolean).join(" ") || undefined
        return (attendee?.bookings ?? []).map((booking) => ({ ...(booking as any), attendeeName } as WithAttendeeName<B>))
    })

export type BookingStatus = "canceled" | "waitlist" | "finished" | "booked"

/** Tag-Darstellung je Status. `key`/`fallback` sind i18n-Args (t(key, fallback)). */
export const STATUS_TAG: Record<BookingStatus, { color: string; key: string; fallback: string }> = {
    canceled: { color: "red", key: "account.status.canceled", fallback: "Storniert" },
    waitlist: { color: "gold", key: "account.status.waitlist", fallback: "Warteliste" },
    finished: { color: "default", key: "account.status.finished", fallback: "Abgeschlossen" },
    booked: { color: "green", key: "account.status.booked", fallback: "Gebucht" },
}

/** Defensives Datums-Parsing: undefined statt Invalid Date (Crash in formatDate). */
export const safeDate = (iso?: string | null): Date | undefined => {
    if (!iso) return undefined
    const date = new Date(iso)
    return Number.isNaN(date.getTime()) ? undefined : date
}

/**
 * Schmales SP-Shape für die Online-Zahlstatus-Ableitung — Teilmenge der
 * AccountBookerBookings-Selektion. `paymentProvider` kommt als String-Enum
 * ("Mollie" | "Manual") aus dem Codegen; hier bewusst lose typisiert, damit
 * der Helper (wie der Rest dieser Datei) nicht an den generierten Typ koppelt.
 */
export interface AccountScheduledPayment {
    id: string
    openAmount?: number | null
    paymentDue?: string | null
    paymentDone?: string | null
    paymentProvider?: string | null
    molliePaymentStatus?: string | null
}

/**
 * Nächste offene Online-Rate (Mollie) einer Buchung — oder undefined, wenn nichts
 * offen ist. "Offen" = Buchung nicht storniert und keine Warteliste, Rate nicht
 * bezahlt (kein paymentDone, Restbetrag > 0, Mollie-Status nicht "paid") und
 * Anbieter Mollie. Bei mehreren offenen Raten die mit der frühesten Fälligkeit
 * (paymentDue ASC) — das ist das Ziel des "Jetzt bezahlen"-Buttons.
 *
 * Warteliste ausgeschlossen: Waitlist-Buchungen haben zwar vollwertige Mollie-SPs
 * mit offenem Betrag, der Mollie-Checkout ist serverseitig aber gesperrt (Initiate
 * wirft UserInputError) — ein "Jetzt bezahlen"-Button liefe ins Leere.
 */
export const findNextOpenOnlinePayment = (booking: {
    canceled?: string | boolean | null
    bookingType?: string | null
    scheduledPayments?: ReadonlyArray<AccountScheduledPayment> | null
}): AccountScheduledPayment | undefined => {
    if (booking.canceled || booking.bookingType === "Waitlist") return undefined
    const open = (booking.scheduledPayments ?? []).filter(
        (sp) => sp.paymentProvider === "Mollie" && !sp.paymentDone && (sp.openAmount == null || sp.openAmount > 0) && sp.molliePaymentStatus !== "paid",
    )
    if (open.length === 0) return undefined
    return [...open].sort((a, b) => (safeDate(a.paymentDue)?.getTime() ?? Infinity) - (safeDate(b.paymentDue)?.getTime() ?? Infinity))[0]
}

export const lessonTimes = (booking: AccountBooking, field: "startDateTime" | "endDateTime"): number[] =>
    (booking.courseLessons ?? [])
        .map((lesson) => lesson[field])
        .filter((value): value is string => Boolean(value))
        .map((value) => new Date(value).getTime())
        .filter((time) => !Number.isNaN(time))

/**
 * Vergangen = storniert ODER letzte Lektion liegt vor `now`. Buchungen ohne
 * Termine gelten konservativ als aktiv (nie vergangen), damit terminlose
 * Buchungen nicht fälschlich als "abgeschlossen" verschwinden.
 */
export const isPastBooking = (booking: AccountBooking, now: Date): boolean => {
    if (booking.canceled) return true
    const ends = lessonTimes(booking, "endDateTime")
    if (ends.length === 0) return false
    return Math.max(...ends) < now.getTime()
}

export const getBookingStatus = (booking: AccountBooking, now: Date): BookingStatus => {
    if (booking.canceled) return "canceled"
    const past = isPastBooking(booking, now)
    if (booking.bookingType === "Waitlist" && !past) return "waitlist"
    if (past) return "finished"
    return "booked"
}

/**
 * Früheste Lektion einer Buchung, die noch in der Zukunft startet (für "Als
 * Nächstes"). Liefert die Lektion inkl. Ort — wichtig, damit Karte/Menü nicht
 * fälschlich Datum und Ort der ersten (ggf. vergangenen) Lektion anzeigen.
 */
export const findNextUpcomingLesson = <L extends AccountBookingLesson>(booking: { courseLessons?: L[] | null }, now: Date): L | undefined => {
    const nowMs = now.getTime()
    let next: L | undefined
    let nextStart = Infinity
    for (const lesson of booking.courseLessons ?? []) {
        if (!lesson.startDateTime) continue
        const start = new Date(lesson.startDateTime).getTime()
        if (Number.isNaN(start) || start <= nowMs) continue
        if (start < nextStart) {
            nextStart = start
            next = lesson
        }
    }
    return next
}

/** Nicht-stornierte Buchung mit der frühesten zukünftigen Startzeit (für "Als Nächstes"). */
export const findNextUpcomingBooking = <T extends AccountBooking>(bookings: T[], now: Date): T | undefined => {
    const nowMs = now.getTime()
    let next: T | undefined
    let nextStart = Infinity
    for (const booking of bookings) {
        if (booking.canceled) continue
        const upcoming = lessonTimes(booking, "startDateTime").filter((time) => time > nowMs)
        if (upcoming.length === 0) continue
        const earliest = Math.min(...upcoming)
        if (earliest < nextStart) {
            nextStart = earliest
            next = booking
        }
    }
    return next
}
